import ReportStore from './ReportStore';
import { action, makeObservable, observable } from 'mobx';
import { BaseListViewStore, BooleanToggleCellData } from 'core/stores';
import { TableFilterDateTemplate, TableFilterTemplate } from 'themes/components';
import {
	DateTimeCellData,
	ReportCategoryCellData,
	ReportTemplateCellData,
	TimeCellData,
} from 'core/stores/CellDataStore';
import {
	TableFilterUtility,
	checkForColumnWidth,
	isSeller,
	isSuperAdminOrganization,
	notificationError,
	translate,
	RemoveFiltersModalUtility,
} from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { localStorageHandler } from 'core/services';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { ApplicationService } from 'common/data';
import _ from 'lodash';
import { DuplicateReport } from 'modules/order/pages';
import { BaasicDropdownViewStore } from 'common/stores';

class ReportListViewStore extends BaseListViewStore {
	@observable selectedReport = null;
	@observable showReports = true;
	fulfillmentOrderColumns = [];
	categoryOptions = [];
	isSuperAdmin = false;
	selectedReport;
	localStorageWidthKey = 'reports-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReportStore(rootStore),
			queryParams: {
				embed: ['reportFrequency', 'organization', 'users', 'reportType', 'reportCategory'],
			},
			findFunction: 'findReports',
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'REPORTS.NAME',
						filterType: TableFilterTemplate,
						sortable: true,
						selected: true,
						dataCell: ReportTemplateCellData,
						filterApiParams: {
							showReports: true,
						},
					},
					{
						key: 'organization.applicationTitle',
						title: 'REPORTS.ORGANIZATION',
						filterType: TableFilterTemplate,
						service: 'applicationService',
						sortable: true,
						selected: true,
						dontSplit: true,
						width: 200,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'reportType.name',
						title: 'REPORTS.TYPE',
						sortable: false,
						selected: true,
					},
					{
						key: 'reportCategory.name',
						title: 'REPORTS.CATEGORY',
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.reportCategory,
						sortable: true,
						selected: true,
						dataCell: ReportCategoryCellData,
					},
					{
						key: 'scheduledDate',
						title: 'REPORTS.SCHEDULED',
						filterType: TableFilterDateTemplate,
						dataCell: DateTimeCellData,
						dateFormat: 'L',
						sortable: true,
						selected: true,
					},
					{
						key: 'sendAt',
						title: 'REPORTS.SENDAT',
						dataCell: TimeCellData,
						fromFormat: 'HH:mm:ss',
						toFormat: 'HH:mm',
						timeField: 'sendAt',
						useLocalTime: true,
						sortable: true,
						selected: true,
					},
					{
						key: 'reportFrequency.name',
						title: 'REPORTS.FREQUENCY',
						filterType: TableFilterTemplate,
						lookupConstant: lookupRouteName.reportFrequency,
						sortable: true,
						selected: true,
					},
					{
						key: 'isActive',
						title: 'REPORTS.ISACTIVE',
						dataCell: BooleanToggleCellData,
						onClick: (report, field) => this.handleIsActiveToggle(report, field),
						sortable: true,
						selected: true,
					},
				],
				sortObject: [
					{
						field: 'name',
						dir: 'asc',
					},
				],
				actions: {
					onDelete: async item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
					onEdit: async item => this.routes.edit(item.id),
					onDuplicate: async item => this.openDuplicateReportModal(item),
					onSendReport: async item => this.openConfirmationModal(this.getSendReportConfirmModalProps(item)),
				},
			},
			permissions: {
				onDelete: () => this.authStore.hasPermission('reportSection.remove'),
				onEdit: () => this.authStore.hasPermission('reportSection.update'),
				onDuplicate: () => this.authStore.hasPermission('reportSection.full'),
				onSendReport: () => this.authStore.hasPermission('reportSection.full'),
			},
			routes: {
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.report.list.edit', {
						id,
					}),
			},
		});
		makeObservable(this);
		this.columnFilterSetup = 'reports-table-data';
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [this.listStore.service, new ApplicationService(this.rootStore.app.baasic.apiClient)],
		});
		this.setSearchVisible();
		this.initialize();
	}

	@action
	initialize = async () => {
		this.isSuperAdmin = isSuperAdminOrganization(this.rootStore);
		this.reportStore = new ReportStore(this.rootStore);
		this.isSeller = isSeller(this.rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.rootStore.viewStore.displaySellerDropdown = false;
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		});
		if (this.isSuperAdmin) {
			await this.initializeOrganizationDropdown();
		}
		await this.queryUtility.initialize(this.findReports);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
	};

	@action
	findReports = async () => {
		this.suspend();
		this.queryUtility.setFilterParams(this, { showReports: this.showReports }, this.tableFilter);
		await this.listStore.findReports(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	};

	@action
	deleteReport = async report => {
		this.suspend();
		try {
			await this.listStore.deleteReport(report);
			this.rootStore.notificationStore.success([
				'REPORTS.DELETESUCCESS',
				{ name: report.name, type: report.reportType.abrv },
			]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	handleIsActiveToggle = async (report, field) => {
		this.suspend();
		try {
			report[field] = !report[field];
			report.reportUsers = report.users;
			await this.listStore.updateReport(
				report,
				this.isTemplate ? sourceOfChange.updateReportTemplate : sourceOfChange.updateReport
			);
			this.rootStore.notificationStore.success([
				'REPORTS.UPDATESUCCESS',
				{ name: name, type: this.isTemplate ? 'template' : 'report' },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action
	sendReport = async item => {
		const notificationId = this.rootStore.notificationStore.load(translate('REPORTS.SENDINGREPORT'));
		try {
			var isSuccess = await this.reportStore.sendReport({ reportId: item.id });
			if (isSuccess) {
				this.rootStore.notificationStore.success(['REPORTS.SENDSUCCESS', { name: item.name }]);
			} else {
				this.rootStore.notificationStore.error(['REPORTS.SENDERROR']);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['REPORTS.SENDERROR']);
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	};

	@action.bound
	initializeOrganizationDropdown = async () => {
		this.organizationDropdownStore = new BaasicDropdownViewStore();
		await this.organizationDropdownStore.initialize({
			store: new ApplicationStore(this.rootStore),
			initCallback: async () => {
				this.queryUtility.filter.organizationIds =
					this.organizationDropdownStore.value == null ? [] : [this.organizationDropdownStore.value.value];
			},
			onChangeCallback: async option => {
				this.queryUtility.filter.organizationIds = option == null ? [] : [option.value];
				await this.findReports();
			},
			localStorageHandlerKey: 'selectedOrganization',
			filterProperties: {
				embed: ['organizationType'],
				excludeSuperAdminOrganization: true,
			},
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
		});
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteReport(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['REPORTS.CONFIRMDELETE', { name: item.name }],
			data: item,
		};
	};

	getSendReportConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.sendReport(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['REPORTS.CONFIRMSENDNOW', { name: item.name }],
			data: item,
		};
	};

	@action
	openDuplicateReportModal = report => {
		this.selectedReport = report;
		this.openModal({
			onSuccess: async () => await this.findReports(),
			data: report.id,
			component: DuplicateReport,
		});
	};

	@action
	onToggle = async value => {
		this.showReports = value;
		_.find(this.tableData.columns, column => {
			return column.key == 'name';
		}).filterApiParams.showReports = value;
		await this.findReports();
	};

	openCreateNewTemplatePage = () => {
		this.rootStore.routerStore.goTo('master.app.main.report.list.add-template');
	};

	openCreateNewReportPage = () => {
		this.rootStore.routerStore.goTo('master.app.main.report.list.add-report');
	};
}

export default ReportListViewStore;
